import { useEffect, useRef, useState } from 'react';
import { useBusinessTypes } from '@/hooks/useBusinessTypes';
import { useCities } from '@/hooks/useCities';
import {
  checkLeadExists,
  createManualLead,
  extractNakedDomain,
  getBusinessType,
  type BusinessTypeMetadata,
} from '@/services/firebase/firestore';
import { IconClose, IconLink, IconGlobeSearch, IconSearch } from '@components/icons';
import { IconButton } from '@components/IconButton';
import { Input } from '@components/Input';
import { Button } from '@components/Button';
import { TypeAhead } from '@components/TypeAhead';
import type { MenuSelectItem } from '@components/MenuSelect';
import styles from './AddLeadModal.module.scss';

export interface AddLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdded?: () => void;
}

const URL_REGEX = /^https?:\/\/[\w.-]+\.[a-z]{2,}(\/.*)?$/i;

const AddLeadForm = ({
  onClose,
  onAdded,
}: Pick<AddLeadModalProps, 'onClose' | 'onAdded'>) => {
  const { businessTypes } = useBusinessTypes();
  const { cities } = useCities();

  const [businessName, setBusinessName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [selectedBusinessType, setSelectedBusinessType] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const businessTypeFieldRef = useRef<HTMLDivElement>(null);
  const cityFieldRef = useRef<HTMLDivElement>(null);

  const isBusinessNameValid = businessName.trim().length > 0;
  const isUrlValid = URL_REGEX.test(websiteUrl.trim());
  const isBusinessTypeValid = selectedBusinessType !== null;
  const isCityValid = selectedCity !== null;
  const isFormValid =
    isBusinessNameValid && isUrlValid && !urlError && isBusinessTypeValid && isCityValid;

  // Debounced duplicate domain check
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!isUrlValid) {
      setUrlError(null);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const domain = extractNakedDomain(websiteUrl.trim());
        const exists = await checkLeadExists(domain);
        setUrlError(exists ? 'A lead with this domain already exists' : null);
      } catch {
        // Silently ignore — server-side check is the fallback
      }
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [websiteUrl, isUrlValid]);

  const businessTypeItems: MenuSelectItem[] = businessTypes.map((bt) => ({
    value: bt.slug,
    label: bt.name,
  }));

  const cityItems: MenuSelectItem[] = cities.map((city) => ({
    value: city.id,
    label: city.name,
    subtitle: city.region ? `${city.region}, ${city.country ?? ''}`.trim() : city.country,
  }));

  const handleAdd = async () => {
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const domain = extractNakedDomain(websiteUrl.trim());
      const selectedType = businessTypes.find((bt) => bt.slug === selectedBusinessType);
      const city = cities.find((c) => c.id === selectedCity);

      if (!selectedType || !city) return;

      // Fetch the full business type to get metadata
      const fullType = await getBusinessType(selectedType.slug);
      const metadata: BusinessTypeMetadata = fullType?.metadata ?? {
        website_objectives: [],
        industry_painpoints: [],
      };

      await createManualLead({
        domain,
        url: websiteUrl.trim(),
        name: businessName.trim(),
        businessTypeSlug: selectedType.slug,
        cityId: city.id,
        cityName: city.name,
        metadata,
      });

      onAdded?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleBackdropKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      onKeyDown={handleBackdropKeyDown}
      role="presentation"
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog needs event handlers */}
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-lead-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.header}>
          <h2 id="add-lead-title" className={styles.title}>
            Add Lead
          </h2>
          <IconButton
            size="small"
            aria-label="Close modal"
            onClick={onClose}
            className={styles.closeButton}
          >
            <IconClose size={18} />
          </IconButton>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Business Name</span>
            <Input
              value={businessName}
              onChange={setBusinessName}
              placeholder="e.g. Alepin Gauthier Avocats"
              clearable
              leadingIcon={<IconGlobeSearch size={18} />}
              className={styles.input}
              disabled={isSubmitting}
            />
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Website URL</span>
            <Input
              value={websiteUrl}
              onChange={setWebsiteUrl}
              placeholder="e.g. https://alepin.com"
              clearable
              leadingIcon={<IconLink size={18} />}
              className={styles.input}
              disabled={isSubmitting}
              error={urlError ?? undefined}
            />
          </div>

          <div
            className={styles.field}
            ref={businessTypeFieldRef}
            onFocusCapture={() => {
              setTimeout(() => {
                businessTypeFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
          >
            <span className={styles.fieldLabel}>Business Type</span>
            <TypeAhead
              items={businessTypeItems}
              value={selectedBusinessType}
              onChange={setSelectedBusinessType}
              placeholder="Search business types..."
              leadingIcon={<IconSearch size={18} />}
              disabled={isSubmitting}
            />
          </div>

          <div
            className={styles.field}
            ref={cityFieldRef}
            onFocusCapture={() => {
              setTimeout(() => {
                cityFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
          >
            <span className={styles.fieldLabel}>City</span>
            <TypeAhead
              items={cityItems}
              value={selectedCity}
              onChange={setSelectedCity}
              placeholder="Search cities..."
              leadingIcon={<IconSearch size={18} />}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAdd} disabled={!isFormValid || isSubmitting}>
            {isSubmitting ? 'Adding...' : 'Add Lead'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const AddLeadModal = ({ isOpen, onClose, onAdded }: AddLeadModalProps) => {
  if (!isOpen) return null;
  return <AddLeadForm onClose={onClose} onAdded={onAdded} />;
};
